/**
 * Collect&Go basket review for an order: mapped articles, sort, qty override.
 */

import {
  MinusIcon,
  PlusIcon,
  RefreshCwIcon,
  SquareIcon,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { toast } from 'sonner'

import { formatCogoChoicePrice, formatCogoPriceCents } from '../lib/cogoPrice'
import {
  choicePackUnitNotice,
  choiceUnitTypeButtons,
  choiceUnitTypes,
  unitTypeLabel,
  unitTypePrimaryBtnClass,
  unitTypeSecondaryBtnClass,
  type UnitType,
} from '../lib/cogoUnits'
import { formatUnit } from '../lib/unit'
import {
  coverageStatus,
  type CogoPlanPick,
  type CoverageStatus,
} from '../lib/cogoPlan'
import type { Product } from './types'

export type CogoPlanLineView = {
  productId: number
  product: Product
  neededQuantity: number
  effectiveQuantity: number
  quantityOverride: number | null
  basketQuantities: Record<string, number>
  skipped: boolean
  status: string
  messageAgent: string
  picks: CogoPlanPick[]
  mapped: boolean
  totalCents: number | null
  unitPriceCents: number | null
  coveredQuantity: number | null
  sortName: string
}

export type CogoBasketCandidate = {
  technicalProductId: string
  display: string
  assortmentUrl?: string | null
  unitLabel?: string | null
  weightConversionFactor?: number | null
  packSize?: number | null
  priceCents?: number | null
  backendUnit?: string | null
  unitTypes?: UnitType[] | null
  isBio?: boolean
  favorite?: boolean
  hasImage?: boolean
}

export type CogoBasketCandInfo = {
  status: string
  articles?: CogoBasketCandidate[]
}

type SortKey = 'alpha' | 'unit' | 'total'

const SORT_STORAGE_KEY = 'cogo-order-basket-sort'

function defaultSortAsc(sort: SortKey): boolean {
  return sort === 'alpha'
}

function loadStoredSort(): { sort: SortKey; sortAsc: boolean } {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY)
    if (!raw) return { sort: 'alpha', sortAsc: true }
    const parsed = JSON.parse(raw) as { sort?: unknown; sortAsc?: unknown }
    const sort: SortKey =
      parsed.sort === 'alpha' ||
      parsed.sort === 'unit' ||
      parsed.sort === 'total'
        ? parsed.sort
        : 'alpha'
    const sortAsc =
      typeof parsed.sortAsc === 'boolean'
        ? parsed.sortAsc
        : defaultSortAsc(sort)
    return { sort, sortAsc }
  } catch {
    return { sort: 'alpha', sortAsc: true }
  }
}

function saveStoredSort(sort: SortKey, sortAsc: boolean) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ sort, sortAsc }))
  } catch {
    /* ignore quota / private mode */
  }
}

function articleImageSrc(technicalProductId: string) {
  return `/api/cogo/articles/${encodeURIComponent(technicalProductId)}/image`
}

function formatMoney(cents: number | null | undefined): string | null {
  if (cents == null || !(cents > 0)) return null
  return formatCogoPriceCents(cents)
}

/**
 * One shared grid for header + every product row so thumbs, amounts
 * and ± buttons stay in the same columns (linked and unlinked).
 */
const LINE_GRID =
  'grid w-full min-w-0 grid-cols-[3rem_minmax(0,1fr)_8.25rem] gap-3 items-start'
/** Same 3-col grid as the − value + stepper (value sits in the middle). */
const QTY_GRID =
  'grid w-full grid-cols-[2rem_minmax(0,1fr)_2rem] items-center gap-1'

function LineGrid({
  thumb,
  main,
  qty,
}: {
  thumb: ReactNode
  main: ReactNode
  qty: ReactNode
}) {
  return (
    <div className={LINE_GRID}>
      <div className="h-12 w-12 justify-self-start rounded overflow-hidden flex items-center justify-center bg-white">
        {thumb}
      </div>
      <div className="min-w-0">{main}</div>
      <div className="min-w-0">{qty}</div>
    </div>
  )
}

function formatQtyInput(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return String(value).replace('.', ',')
}

function parseQtyInput(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.')
  if (!trimmed) return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return NaN
  return n
}

function qtyStep(unit: string): number {
  const u = (unit || '').toLowerCase()
  if (u.includes('gram') || u === 'g') return 50
  return 1
}

function roundQty(value: number, unit: string): number {
  const step = qtyStep(unit)
  if (step >= 1 && Number.isInteger(step)) {
    return Math.round(value / step) * step
  }
  return Math.round(value * 1000) / 1000
}

function underNeeded(line: CogoPlanLineView): boolean {
  return (
    !line.skipped &&
    line.effectiveQuantity < line.neededQuantity &&
    line.neededQuantity > 0
  )
}

function lineCoverage(line: CogoPlanLineView): CoverageStatus | null {
  if (line.skipped || line.picks.length === 0) return null
  return coverageStatus(line.coveredQuantity, line.neededQuantity)
}

function formatPickUnits(pick: CogoPlanPick): string {
  if (pick.quantityUnit === 'kg') {
    return formatQtyInput(pick.units)
  }
  return Number.isInteger(pick.units)
    ? String(pick.units)
    : formatQtyInput(pick.units)
}

function pickStep(pick: CogoPlanPick): number {
  return pick.quantityUnit === 'kg' ? 0.1 : 1
}

function roundPickUnits(value: number, pick: CogoPlanPick): number {
  const step = pickStep(pick)
  if (step >= 1) return Math.round(value / step) * step
  return Math.round(value * 10) / 10
}

const btnSecondary =
  'inline-flex items-center justify-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium bg-slate-100 text-slate-800 ring-1 ring-slate-300 hover:bg-slate-200 hover:ring-slate-400 disabled:opacity-40'
const btnStepper =
  'inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-800 ring-1 ring-slate-300 hover:bg-slate-200 hover:ring-slate-400 disabled:opacity-40'
const btnIcon =
  'inline-flex h-8 w-8 items-center justify-center rounded-full opacity-50 hover:opacity-100 disabled:opacity-40'

export function CogoBasketSyncSummary({
  pendingCount,
  className,
}: {
  pendingCount: number
  className?: string
}) {
  const text =
    pendingCount > 0
      ? `${pendingCount} artikel${pendingCount === 1 ? '' : 'en'} nog te syncen`
      : 'Alles gesynchroniseerd'
  return (
    <p className={`text-xs opacity-50 ${className ?? ''}`}>{text}</p>
  )
}

/** Official Collect&Go mark (logo-mobile.svg). */
function CogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M32 15.9887C32.0022 19.1537 31.0657 22.2482 29.309 24.8809C27.5522 27.5136 25.0542 29.5661 22.1307 30.7788C19.2073 31.9916 15.9899 32.31 12.8855 31.6939C9.78103 31.0778 6.92905 29.5549 4.69028 27.3177C2.45151 25.0805 0.926533 22.2296 0.308254 19.1256C-0.310025 16.0216 0.00616878 12.804 1.21684 9.87968C2.42751 6.9554 4.47826 4.45588 7.10969 2.69727C9.74112 0.938672 12.835 2.22223e-06 16 1.43454e-06C18.1004 -0.000888168 20.1805 0.411982 22.1214 1.21504C24.0622 2.01809 25.8259 3.1956 27.3117 4.68032C28.7975 6.16504 29.9762 7.92788 30.7806 9.86819C31.585 11.8085 31.9994 13.8883 32 15.9887Z"
        fill="#0055A2"
      />
      <path
        d="M6.31917 14.3022L7.1545 22.4298C7.19694 22.9453 7.43141 23.426 7.81148 23.7768C8.19155 24.1276 8.68949 24.3229 9.20671 24.324H22.6759C23.1944 24.3266 23.6946 24.1327 24.0759 23.7812C24.4571 23.4297 24.6909 22.9468 24.7303 22.4298L25.5634 14.3022H6.31917Z"
        fill="white"
      />
      <path
        d="M25.428 11.1663H17.849L20.6463 6.92421C20.7812 6.73466 20.8451 6.50369 20.8269 6.27175C20.8219 6.08381 20.7722 5.89976 20.6819 5.73486C20.5916 5.56996 20.4633 5.42895 20.3076 5.32353C20.222 5.26946 20.1313 5.22408 20.0367 5.18808C20.0141 5.18808 19.9915 5.1655 19.969 5.1655C19.8808 5.13948 19.7898 5.12431 19.698 5.12035H19.6755C19.5852 5.12035 19.4949 5.14292 19.4271 5.14292C19.4045 5.14292 19.382 5.1655 19.3594 5.1655C19.2917 5.18808 19.2465 5.21065 19.1788 5.23323C19.1562 5.25581 19.1336 5.25581 19.1111 5.27838C19.0532 5.30822 19 5.34624 18.953 5.39126C18.9304 5.41384 18.9079 5.41384 18.8853 5.43642C18.8189 5.49838 18.7584 5.56641 18.7047 5.63961L17.5758 7.33059L15.0744 11.1438H6.5901C5.7796 11.1438 5.23551 11.6179 5.23551 12.3177C5.22994 12.6126 5.33998 12.8979 5.54207 13.1127C5.74417 13.3274 6.02229 13.4546 6.31692 13.4669H25.674C25.9707 13.458 26.2519 13.3327 26.4568 13.1181C26.6617 12.9034 26.7739 12.6167 26.769 12.32C26.8142 11.6427 26.2723 11.1686 25.437 11.1686"
        fill="white"
      />
      <path
        d="M11.6856 17.8964V16.7021C11.6856 16.1377 12.0243 15.7765 12.4984 15.7765C12.9725 15.7765 13.2886 16.1377 13.2886 16.6796V17.919C13.2886 18.4834 12.9725 18.7995 12.4984 18.7995C12.0243 18.7995 11.6856 18.4608 11.6856 17.8964Z"
        fill="#0055A2"
      />
      <path
        d="M20.8991 20.4024C20.5049 21.1421 19.9459 21.7811 19.2653 22.2701C18.5846 22.7591 17.8006 23.085 16.9738 23.2225C16.1471 23.36 15.2998 23.3054 14.4975 23.063C13.6952 22.8207 12.9594 22.397 12.3471 21.8248C12.2691 21.7595 12.2051 21.6792 12.1591 21.5885C12.1131 21.4979 12.0859 21.3989 12.0792 21.2974C12.0725 21.196 12.0865 21.0942 12.1202 20.9983C12.154 20.9024 12.2069 20.8144 12.2756 20.7395C12.3444 20.6646 12.4276 20.6045 12.5203 20.5627C12.613 20.5209 12.7132 20.4983 12.8148 20.4964C12.9165 20.4944 13.0175 20.5131 13.1117 20.5513C13.2059 20.5894 13.2914 20.6463 13.363 20.7185C13.8122 21.1356 14.3511 21.444 14.9382 21.6201C15.5253 21.7963 16.1449 21.8354 16.7495 21.7344C17.3511 21.6294 17.9213 21.3902 18.4177 21.0345C18.914 20.6788 19.3239 20.2158 19.6167 19.68C19.6637 19.5938 19.7272 19.5178 19.8037 19.4563C19.8802 19.3949 19.9681 19.3492 20.0624 19.3219C20.1567 19.2946 20.2554 19.2863 20.3529 19.2974C20.4504 19.3084 20.5447 19.3387 20.6304 19.3865C20.7981 19.488 20.92 19.6505 20.9705 19.8399C21.021 20.0293 20.9962 20.2309 20.9013 20.4024"
        fill="#0055A2"
      />
      <path
        d="M21.7728 17.7835C21.68 17.8471 21.5691 17.8788 21.4567 17.8738C21.3575 17.8762 21.2593 17.8524 21.1722 17.8048C21.085 17.7573 21.0119 17.6876 20.9601 17.6029C20.8786 17.4762 20.7646 17.3737 20.63 17.3062C20.4953 17.2387 20.345 17.2086 20.1947 17.2191H20.1721C20.025 17.2109 19.8784 17.2421 19.7474 17.3096C19.6164 17.377 19.5058 17.4783 19.4271 17.6029C19.3391 17.7244 19.2081 17.8077 19.0609 17.8359C18.9136 17.8641 18.7611 17.8351 18.6344 17.7548C18.5078 17.6745 18.4165 17.5489 18.3792 17.4037C18.3419 17.2584 18.3614 17.1044 18.4337 16.973C18.6181 16.6879 18.8729 16.455 19.1734 16.2968C19.4739 16.1386 19.8101 16.0605 20.1496 16.07C20.5016 16.0543 20.8516 16.1316 21.1642 16.2941C21.4769 16.4566 21.7413 16.6985 21.9309 16.9956C21.9763 17.0563 22.0084 17.126 22.0251 17.1999C22.0418 17.2739 22.0427 17.3505 22.0278 17.4249C22.0129 17.4992 21.9825 17.5696 21.9385 17.6314C21.8946 17.6932 21.8381 17.745 21.7728 17.7835Z"
        fill="#0055A2"
      />
    </svg>
  )
}

export default function CogoOrderBasket({
  lines,
  candidatesByProduct,
  enrichSearching = false,
  enrichClaimProductId = null,
  onOverride,
  onBasketQuantities,
  onChangeArticle,
  onPickCandidate,
  onSearchEmpty,
  onMoveToStorage,
  basketSummary,
}: {
  lines: CogoPlanLineView[]
  candidatesByProduct?: Record<string, CogoBasketCandInfo>
  /** True while background enrich is actively claiming/scraping. */
  enrichSearching?: boolean
  enrichClaimProductId?: number | null
  onOverride: (
    productId: number,
    quantityOverride: number | null
  ) => Promise<void>
  onBasketQuantities: (
    productId: number,
    basketQuantities: Record<string, number>
  ) => Promise<void>
  onChangeArticle: (line: CogoPlanLineView) => void
  /** Resolves false when the pick was rejected (keeps the picker open). */
  onPickCandidate: (
    line: CogoPlanLineView,
    opt: CogoBasketCandidate,
    unitType: UnitType | null
  ) => Promise<boolean>

  onSearchEmpty?: (line: CogoPlanLineView, searchTerm: string) => void
  onMoveToStorage?: (productId: number) => void
  basketSummary?: ReactNode
}) {
  const [sort, setSort] = useState<SortKey>(() => loadStoredSort().sort)
  /** alpha defaults asc; unit/total default desc (high → low). */
  const [sortAsc, setSortAsc] = useState(() => loadStoredSort().sortAsc)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [mappingKey, setMappingKey] = useState<string | null>(null)
  const [changingId, setChangingId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    saveStoredSort(sort, sortAsc)
  }, [sort, sortAsc])

  const sorted = useMemo(() => {
    const active = lines.filter((l) => !l.skipped)
    const skipped = lines.filter((l) => l.skipped)
    const nullSentinel = sortAsc
      ? Number.POSITIVE_INFINITY
      : Number.NEGATIVE_INFINITY
    const dir = sortAsc ? 1 : -1
    const cmp = (a: CogoPlanLineView, b: CogoPlanLineView) => {
      // Unmapped (not linked to a Cogo article) always first.
      const am = a.mapped ? 1 : 0
      const bm = b.mapped ? 1 : 0
      if (am !== bm) return am - bm
      if (sort === 'unit') {
        const au = a.unitPriceCents ?? nullSentinel
        const bu = b.unitPriceCents ?? nullSentinel
        if (au !== bu) return (au - bu) * dir
      } else if (sort === 'total') {
        const at = a.totalCents ?? nullSentinel
        const bt = b.totalCents ?? nullSentinel
        if (at !== bt) return (at - bt) * dir
      }
      const an = a.product.display || ''
      const bn = b.product.display || ''
      return an.localeCompare(bn, 'nl') * dir
    }
    return [...active.sort(cmp), ...skipped.sort(cmp)]
  }, [lines, sort, sortAsc])

  useEffect(() => {
    if (editingId == null) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [editingId])

  function startEdit(line: CogoPlanLineView) {
    setEditingId(line.productId)
    setDraft(
      formatQtyInput(
        line.quantityOverride != null
          ? line.quantityOverride
          : line.neededQuantity
      )
    )
  }

  function cancelEdit() {
    setEditingId(null)
    setDraft('')
  }

  async function saveOverride(
    line: CogoPlanLineView,
    quantityOverride: number | null
  ) {
    setBusyId(line.productId)
    try {
      await onOverride(line.productId, quantityOverride)
      cancelEdit()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Opslaan mislukt')
    } finally {
      setBusyId(null)
    }
  }

  async function submitDraft(line: CogoPlanLineView) {
    const parsed = parseQtyInput(draft)
    if (parsed !== null && Number.isNaN(parsed)) {
      toast.error('Ongeldige hoeveelheid')
      return
    }
    await saveOverride(line, parsed)
  }

  async function nudge(line: CogoPlanLineView, direction: 1 | -1) {
    const step = qtyStep(line.product.unit)
    const current =
      line.quantityOverride != null
        ? line.quantityOverride
        : line.neededQuantity
    const next = roundQty(
      Math.max(0, current + direction * step),
      line.product.unit
    )
    const override = next === line.neededQuantity ? null : next
    await saveOverride(line, override)
  }

  async function nudgePick(
    line: CogoPlanLineView,
    pick: CogoPlanPick,
    direction: 1 | -1
  ) {
    const step = pickStep(pick)
    const next = roundPickUnits(
      Math.max(0, pick.units + direction * step),
      pick
    )
    const base: Record<string, number> = {
      ...(line.basketQuantities || {}),
    }
    if (Object.keys(base).length === 0) {
      for (const p of line.picks) base[p.technicalProductId] = p.units
    }
    base[pick.technicalProductId] = next
    setBusyId(line.productId)
    try {
      await onBasketQuantities(line.productId, base)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Opslaan mislukt')
    } finally {
      setBusyId(null)
    }
  }

  async function pickCandidate(
    line: CogoPlanLineView,
    opt: CogoBasketCandidate,
    unitType: UnitType | null
  ) {
    const key = `${line.productId}:${opt.technicalProductId}:${unitType ?? ''}`
    setMappingKey(key)
    try {
      if (await onPickCandidate(line, opt, unitType)) setChangingId(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Mappen mislukt')
    } finally {
      setMappingKey(null)
    }
  }

  const activeCount = useMemo(
    () => lines.filter((l) => !l.skipped).length,
    [lines]
  )
  const listTotalCents = useMemo(() => {
    let sum = 0
    let any = false
    for (const l of lines) {
      if (l.skipped) continue
      if (l.totalCents != null) {
        sum += l.totalCents
        any = true
      }
    }
    return any ? sum : null
  }, [lines])
  const listTotalLabel = formatMoney(listTotalCents)

  if (lines.length === 0) {
    return (
      <p className="text-sm opacity-40 py-1">Geen producten om te bestellen</p>
    )
  }

  return (
    <div className="w-full min-w-0 flex flex-col gap-3">
      {basketSummary ? (
        <div className="flex flex-col gap-2">{basketSummary}</div>
      ) : null}
      <div className="flex items-baseline gap-3 min-w-0">
        {onMoveToStorage ? (
          <span className="w-6 shrink-0" aria-hidden />
        ) : null}
        <div className="min-w-0 flex-1">
          <LineGrid
            thumb={<span aria-hidden />}
            main={
              <span className="text-sm font-medium opacity-70">
                {activeCount} {activeCount === 1 ? 'artikel' : 'artikelen'}
              </span>
            }
            qty={
              <div className={QTY_GRID}>
                <span aria-hidden />
                <span className="min-w-0 text-center text-sm font-medium tabular-nums truncate">
                  {listTotalLabel || (
                    <span className="opacity-40 text-xs font-normal">
                      geen prijs
                    </span>
                  )}
                </span>
                <span aria-hidden />
              </div>
            }
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="opacity-50">Sorteer</span>
        {(
          [
            ['alpha', 'A–Z'],
            ['unit', 'Eenheidsprijs'],
            ['total', 'Totaal'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              if (sort === key) {
                setSortAsc((asc) => !asc)
              } else {
                setSort(key)
                setSortAsc(key === 'alpha')
              }
            }}
            className={
              sort === key
                ? 'rounded px-2 py-1 bg-slate-200 font-medium'
                : 'rounded px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600'
            }
          >
            {label}
            {sort === key ? (sortAsc ? ' ↑' : ' ↓') : null}
          </button>
        ))}
      </div>

      <ul className="min-w-0 flex flex-col divide-y divide-slate-200">
        {sorted.map((line) => {
          const unit = formatUnit(line.product.unit) ?? ''
          const overridden =
            line.quantityOverride != null &&
            line.quantityOverride !== line.neededQuantity
          const statusLabel =
            line.status === 'ok'
              ? line.messageAgent || 'Op Collect&Go'
              : line.messageAgent || null
          const editing = editingId === line.productId
          const busy = busyId === line.productId
          const singlePick = line.picks.length === 1 ? line.picks[0]! : null
          const cand = candidatesByProduct?.[String(line.productId)]

          const changing = changingId === line.productId
          const needsAction = !line.mapped || changing || editing

          return (
            <li
              key={line.productId}
              className={
                'py-3 min-w-0 w-full flex gap-3 first:pt-1 last:pb-1' +
                (needsAction ? ' bg-slate-50 rounded-md' : '') +
                (line.skipped && !editing ? ' opacity-45' : '')
              }
            >
              {onMoveToStorage ? (
                line.skipped ? (
                  <span className="w-6 shrink-0" aria-hidden />
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    className="w-6 h-6 shrink-0 mt-1 inline-flex items-center justify-center focus:outline-hidden disabled:opacity-40"
                    title="Verplaats naar voorraad"
                    aria-label={`${line.product.display} naar voorraad`}
                    aria-checked={false}
                    role="checkbox"
                    onClick={() => onMoveToStorage(line.productId)}
                  >
                    <SquareIcon className="h-5 w-5 opacity-30" />
                  </button>
                )
              ) : null}
              <div className="min-w-0 flex-1 flex flex-col gap-2">
              {singlePick ? (
                <SinglePickRow
                  line={line}
                  pick={singlePick}
                  unit={unit}
                  statusLabel={statusLabel}
                  busy={busy}
                  cand={cand}
                  changing={changing}
                  searching={
                    enrichSearching &&
                    (enrichClaimProductId == null ||
                      enrichClaimProductId === line.productId ||
                      cand?.status === 'pending')
                  }
                  mappingKey={mappingKey}
                  onNudgePick={(dir) => void nudgePick(line, singlePick, dir)}
                  onToggleChange={() =>
                    setChangingId((id) =>
                      id === line.productId ? null : line.productId
                    )
                  }
                  onPickCandidate={(opt, mode) =>
                    void pickCandidate(line, opt, mode)
                  }
                  onSearchEmpty={(term) =>
                    onSearchEmpty
                      ? onSearchEmpty(line, term)
                      : onChangeArticle(line)
                  }
                />
              ) : (
                <ProductRow
                  line={line}
                  unit={unit}
                  overridden={overridden}
                  statusLabel={statusLabel}
                  busy={busy}
                  cand={cand}
                  changing={changing}
                  searching={
                    enrichSearching &&
                    (enrichClaimProductId == null ||
                      enrichClaimProductId === line.productId ||
                      cand?.status === 'pending')
                  }
                  mappingKey={mappingKey}
                  onNudge={(dir) => void nudge(line, dir)}
                  onEditQty={() => startEdit(line)}
                  onToggleChange={() =>
                    setChangingId((id) =>
                      id === line.productId ? null : line.productId
                    )
                  }
                  onPickCandidate={(opt, mode) =>
                    void pickCandidate(line, opt, mode)
                  }
                  onSearchEmpty={(term) =>
                    onSearchEmpty
                      ? onSearchEmpty(line, term)
                      : onChangeArticle(line)
                  }
                />
              )}

              {editing ? (
                <QtyEditForm
                  line={line}
                  unit={unit}
                  draft={draft}
                  busy={busy}
                  inputRef={inputRef}
                  setDraft={setDraft}
                  onSubmit={() => void submitDraft(line)}
                  onRecipe={() => void saveOverride(line, null)}
                  onSkip={() => void saveOverride(line, 0)}
                  onCancel={cancelEdit}
                />
              ) : null}

              {!singlePick && line.picks.length > 1 ? (
                <ul className="flex flex-col gap-2">
                  {line.picks.map((pick) => (
                    <PickRow
                      key={pick.technicalProductId + pick.label}
                      pick={pick}
                      busy={busy}
                      onNudge={(dir) => void nudgePick(line, pick, dir)}
                    />
                  ))}
                </ul>
              ) : null}

              {!singlePick && line.picks.length > 0 ? (
                <CoverageNotice line={line} unit={unit} />
              ) : null}

              {!singlePick &&
              line.picks.length === 0 &&
              line.mapped &&
              !line.skipped ? (
                <p className="text-sm opacity-50">
                  Geen omzetting van {formatUnit(line.product.unit)} naar dit
                  artikel — wijzig artikel of hoeveelheid
                </p>
              ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function formatMenuQty(value: number, unit: string): string {
  const n = Number.isInteger(value) ? String(value) : formatQtyInput(value)
  return unit ? `${n} ${unit}` : n
}

function CoverageNotice({
  line,
  unit,
}: {
  line: CogoPlanLineView
  unit: string
}) {
  const status = lineCoverage(line)
  if (!status || status === 'exact') return null
  const covered = line.coveredQuantity
  const covLabel =
    covered != null ? formatMenuQty(covered, unit) : '—'
  const needLabel = formatMenuQty(line.neededQuantity, unit)
  if (status === 'under') {
    const short =
      covered != null
        ? formatMenuQty(line.neededQuantity - covered, unit)
        : null
    return (
      <p className="text-xs text-amber-700">
        Te weinig voor recept: {covLabel} i.p.v. {needLabel}
        {short ? ` (−${short})` : ''}
      </p>
    )
  }
  const extra =
    covered != null
      ? formatMenuQty(covered - line.neededQuantity, unit)
      : null
  return (
    <p className="text-xs text-sky-700">
      Meer dan recept: {covLabel} i.p.v. {needLabel}
      {extra ? ` (+${extra})` : ''}
    </p>
  )
}

/** Shared − value + stepper so every row’s amount sits in the same column. */
function QtyStepper({
  value,
  busy,
  minusDisabled,
  onMinus,
  onPlus,
  onValueClick,
  valueTitle,
}: {
  value: string
  busy: boolean
  minusDisabled?: boolean
  onMinus: () => void
  onPlus: () => void
  onValueClick?: () => void
  valueTitle?: string
}) {
  const valueClass =
    'w-full min-w-0 text-center text-sm font-medium tabular-nums px-0.5 truncate'
  return (
    <div className={QTY_GRID}>
      <button
        type="button"
        disabled={busy || minusDisabled}
        className={btnStepper}
        aria-label="Minder"
        onClick={onMinus}
      >
        <MinusIcon className="h-4 w-4" />
      </button>
      {onValueClick ? (
        <button
          type="button"
          disabled={busy}
          className={`${valueClass} rounded-md py-1 hover:bg-slate-100 disabled:opacity-40`}
          title={valueTitle}
          onClick={onValueClick}
        >
          {value}
        </button>
      ) : (
        <span className={valueClass}>{value}</span>
      )}
      <button
        type="button"
        disabled={busy}
        className={btnStepper}
        aria-label="Meer"
        onClick={onPlus}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

function ArticleQtyControls({
  pick,
  busy,
  onNudge,
}: {
  pick: CogoPlanPick
  busy: boolean
  onNudge: (dir: 1 | -1) => void
}) {
  const suffix = pick.quantityUnit === 'kg' ? ' kg' : ''
  return (
    <QtyStepper
      value={`${formatPickUnits(pick)}${suffix}`}
      busy={busy}
      minusDisabled={pick.units <= 0}
      onMinus={() => onNudge(-1)}
      onPlus={() => onNudge(1)}
    />
  )
}

function UnderNeededNotice({
  line,
  unit,
}: {
  line: CogoPlanLineView
  unit: string
}) {
  // Product-level under (unmapped / no picks) — mapped uses CoverageNotice
  if (line.picks.length > 0) return null
  if (!underNeeded(line)) return null
  return (
    <p className="text-xs text-amber-700">
      Minder dan recept: {line.effectiveQuantity}
      {unit ? ` ${unit}` : ''} i.p.v. {line.neededQuantity}
      {unit ? ` ${unit}` : ''}
    </p>
  )
}

function QtyControls({
  line,
  unit,
  overridden,
  busy,
  onNudge,
  onEditQty,
}: {
  line: CogoPlanLineView
  unit: string
  overridden: boolean
  busy: boolean
  onNudge: (dir: 1 | -1) => void
  onEditQty: () => void
}) {
  const value = line.skipped
    ? '0'
    : `${line.effectiveQuantity}${unit ? ` ${unit}` : ''}`
  return (
    <QtyStepper
      value={value}
      busy={busy}
      minusDisabled={line.effectiveQuantity <= 0}
      onMinus={() => onNudge(-1)}
      onPlus={() => onNudge(1)}
      onValueClick={onEditQty}
      valueTitle={
        overridden
          ? `Bewerken (recept ${line.neededQuantity}${unit ? ` ${unit}` : ''})`
          : 'Hoeveelheid bewerken'
      }
    />
  )
}

function SinglePickRow({
  line,
  pick,
  unit,
  statusLabel,
  busy,
  cand,
  changing,
  searching,
  mappingKey,
  onNudgePick,
  onToggleChange,
  onPickCandidate,
  onSearchEmpty,
}: {
  line: CogoPlanLineView
  pick: CogoPlanPick
  unit: string
  statusLabel: string | null
  busy: boolean
  cand: CogoBasketCandInfo | undefined
  changing: boolean
  searching: boolean
  mappingKey: string | null
  onNudgePick: (dir: 1 | -1) => void
  onToggleChange: () => void
  onPickCandidate: (opt: CogoBasketCandidate, unitType: UnitType | null) => void
  onSearchEmpty: (searchTerm: string) => void
}) {
  const unitPrice = formatCogoChoicePrice(pick)
  const total = formatMoney(line.totalCents ?? pick.lineTotalCents)
  const articles = mergeCurrentIntoCandidates(cand?.articles || [], pick)

  return (
    <div className="w-full min-w-0 flex flex-col gap-2">
      <LineGrid
        thumb={
          pick.hasImage ? (
            <img
              src={articleImageSrc(pick.technicalProductId)}
              alt=""
              className="h-full w-full object-contain object-top"
            />
          ) : (
            <span className="text-[10px] opacity-40">geen foto</span>
          )
        }
        main={
          <div className="flex flex-col gap-2">
            <div className="font-semibold break-words leading-snug">
              {pick.display}
            </div>
            <div className="text-xs opacity-60 break-words">
              voor {line.product.display}
              {unitPrice ? ` · ${unitPrice}` : ''}
              {total ? ` · ${total}` : ''}
            </div>
            {statusLabel ? (
              <div className="text-xs text-lime-600">{statusLabel}</div>
            ) : null}
            <CoverageNotice line={line} unit={unit} />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={
                  changing
                    ? 'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium bg-violet-100 text-violet-800 ring-1 ring-violet-200'
                    : btnSecondary
                }
                onClick={onToggleChange}
                title={changing ? 'Sluiten' : 'Artikel wijzigen'}
                aria-expanded={changing}
              >
                <RefreshCwIcon className="h-3.5 w-3.5" />
                {changing ? 'sluiten' : 'artikel'}
              </button>
              {pick.assortmentUrl ? (
                <a
                  href={pick.assortmentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={btnIcon}
                  title="Open op Collect&Go"
                  aria-label="Open op Collect&Go"
                >
                  <CogoMark className="h-6 w-6" />
                </a>
              ) : null}
            </div>
          </div>
        }
        qty={
          <ArticleQtyControls pick={pick} busy={busy} onNudge={onNudgePick} />
        }
      />
      {changing ? (
        <CandidateList
          articles={articles}
          status={cand?.status ?? (articles.length > 0 ? 'ready' : null)}
          searching={searching}
          mappingKey={mappingKey}
          productId={line.productId}
          productDisplay={line.product.display}
          onPick={onPickCandidate}
          onSearchEmpty={onSearchEmpty}
        />
      ) : null}
    </div>
  )
}

function mergeCurrentIntoCandidates(
  articles: CogoBasketCandidate[],
  pick: CogoPlanPick
): CogoBasketCandidate[] {
  const current: CogoBasketCandidate = {
    technicalProductId: pick.technicalProductId,
    display: pick.display,
    assortmentUrl: pick.assortmentUrl,
    unitLabel: pick.unitLabel,
    weightConversionFactor: pick.weightConversionFactor,
    packSize: pick.packSize,
    priceCents: pick.priceCents,
    backendUnit: pick.backendUnit,
    unitTypes: pick.unitType ? [pick.unitType] : null,
    hasImage: pick.hasImage,
  }
  const byTpc = new Map<string, CogoBasketCandidate>()
  byTpc.set(current.technicalProductId, current)
  for (const a of articles) {
    if (!byTpc.has(a.technicalProductId)) byTpc.set(a.technicalProductId, a)
  }
  // Keep current selection first.
  return [
    current,
    ...[...byTpc.values()].filter(
      (a) => a.technicalProductId !== current.technicalProductId
    ),
  ]
}

function ProductRow({
  line,
  unit,
  overridden,
  statusLabel,
  busy,
  cand,
  changing,
  searching,
  mappingKey,
  onNudge,
  onEditQty,
  onToggleChange,
  onPickCandidate,
  onSearchEmpty,
}: {
  line: CogoPlanLineView
  unit: string
  overridden: boolean
  statusLabel: string | null
  busy: boolean
  cand: CogoBasketCandInfo | undefined
  changing: boolean
  searching: boolean
  mappingKey: string | null
  onNudge: (dir: 1 | -1) => void
  onEditQty: () => void
  onToggleChange: () => void
  onPickCandidate: (opt: CogoBasketCandidate, unitType: UnitType | null) => void
  onSearchEmpty: (searchTerm: string) => void
}) {
  const articles = cand?.articles || []
  const candStatus = cand?.status ?? null
  const total = formatMoney(line.totalCents)

  return (
    <div className="w-full min-w-0 flex flex-col gap-2">
      <LineGrid
        thumb={<span aria-hidden />}
        main={
          <div>
            <div className="font-semibold break-words">
              {line.product.display}
            </div>
            {overridden && !line.skipped ? (
              <div className="text-xs opacity-60 mt-0.5">
                recept {line.neededQuantity}
                {unit ? ` ${unit}` : ''}
                {total ? ` · ${total}` : ''}
              </div>
            ) : total ? (
              <div className="text-xs opacity-60 mt-0.5 tabular-nums">
                {total}
              </div>
            ) : null}
            {statusLabel ? (
              <div className="text-xs text-lime-600 mt-0.5">{statusLabel}</div>
            ) : null}
            <UnderNeededNotice line={line} unit={unit} />
          </div>
        }
        qty={
          <QtyControls
            line={line}
            unit={unit}
            overridden={overridden}
            busy={busy}
            onNudge={onNudge}
            onEditQty={onEditQty}
          />
        }
      />

      {line.mapped ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={
                changing
                  ? 'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium bg-violet-100 text-violet-800 ring-1 ring-violet-200'
                  : btnSecondary
              }
              onClick={onToggleChange}
              title={changing ? 'Sluiten' : 'Artikel wijzigen'}
              aria-expanded={changing}
            >
              <RefreshCwIcon className="h-3.5 w-3.5" />
              {changing ? 'sluiten' : 'artikel'}
            </button>
          </div>
          {changing ? (
            <CandidateList
              articles={articles}
              status={candStatus}
              searching={searching}
              mappingKey={mappingKey}
              productId={line.productId}
              productDisplay={line.product.display}
              onPick={onPickCandidate}
              onSearchEmpty={onSearchEmpty}
            />
          ) : null}
        </div>
      ) : (
        <CandidateList
          articles={articles}
          status={candStatus}
          searching={searching}
          mappingKey={mappingKey}
          productId={line.productId}
          productDisplay={line.product.display}
          onPick={onPickCandidate}
          onSearchEmpty={onSearchEmpty}
        />
      )}
    </div>
  )
}

function CandidateList({
  articles,
  status,
  searching,
  mappingKey,
  productId,
  productDisplay,
  onPick,
  onSearchEmpty,
}: {
  articles: CogoBasketCandidate[]
  status: string | null
  searching: boolean
  mappingKey: string | null
  productId: number
  productDisplay: string
  onPick: (opt: CogoBasketCandidate, unitType: UnitType | null) => void
  onSearchEmpty: (searchTerm: string) => void
}) {
  const [searchQ, setSearchQ] = useState(productDisplay)

  useEffect(() => {
    setSearchQ(productDisplay)
  }, [productDisplay])

  function submitSearch() {
    onSearchEmpty(searchQ)
  }

  function hasPdp(opt: CogoBasketCandidate) {
    const url = opt.assortmentUrl?.trim() || ''
    return Boolean(url && /\/assortiment\//i.test(url) && !/rootCategoryId=/i.test(url))
  }

  function pickOrExplain(opt: CogoBasketCandidate, mode: UnitType | null) {
    if (!hasPdp(opt)) return
    onPick(opt, mode)
  }

  const searchControls = (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <input
        type="search"
        value={searchQ}
        disabled={searching}
        onChange={(e) => setSearchQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submitSearch()
          }
        }}
        placeholder="Zoekterm…"
        aria-label={`Zoek Collect&Go voor ${productDisplay}`}
        className="min-w-0 flex-1 basis-40 rounded border border-slate-300 bg-white px-2 py-1.5 text-sm outline-hidden focus:ring-2 focus:ring-sky-300 disabled:opacity-50"
      />
      <button
        type="button"
        className={btnSecondary}
        disabled={searching}
        onClick={submitSearch}
      >
        {articles.length > 0 ? 'Meer artikelen…' : 'Zoek op Collect&Go'}
      </button>
    </div>
  )

  if (articles.length > 0) {
    return (
      <ul className="flex flex-col gap-1.5">
        {articles.map((opt) => {
          const modes = choiceUnitTypes(opt)
          const multi = modes.length > 1
          const buttons = choiceUnitTypeButtons(modes)
          const packNotice = choicePackUnitNotice(modes)
          const price = formatCogoChoicePrice(opt)
          const meta = [
            opt.unitLabel,
            opt.weightConversionFactor
              ? `${opt.weightConversionFactor}g`
              : null,
            opt.packSize && opt.packSize > 1 ? `${opt.packSize}st` : null,
            opt.isBio ? 'BIO' : null,
            opt.favorite ? 'favoriet' : null,
          ]
            .filter(Boolean)
            .join(' · ')
          const busyThis = mappingKey?.startsWith(
            `${productId}:${opt.technicalProductId}:`
          )

          return (
            <li key={opt.technicalProductId}>
              {multi ? (
                <div className="flex gap-2.5 items-start rounded-md bg-white px-2 py-2 ring-1 ring-slate-200">
                  <CandidateThumb opt={opt} />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-sm font-medium leading-snug break-words">
                      {opt.display}
                    </div>
                    {meta ? (
                      <div className="text-xs opacity-60 mt-0.5">{meta}</div>
                    ) : null}
                    {!hasPdp(opt) ? (
                      <div className="text-xs text-amber-800 mt-0.5">
                        Geen productpagina — zoek opnieuw in Collect&Go
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {buttons.map((mode, i) => (
                        <button
                          key={mode}
                          type="button"
                          disabled={Boolean(mappingKey) || !hasPdp(opt)}
                          className={
                            i === 0
                              ? unitTypePrimaryBtnClass
                              : unitTypeSecondaryBtnClass
                          }
                          onClick={() => pickOrExplain(opt, mode)}
                        >
                          {unitTypeLabel(mode)}
                        </button>
                      ))}
                    </div>
                    {packNotice ? (
                      <button
                        type="button"
                        disabled={Boolean(mappingKey) || !hasPdp(opt)}
                        className="mt-1 text-xs text-sky-700 hover:underline disabled:opacity-50"
                        onClick={() => pickOrExplain(opt, 'PA')}
                      >
                        Ook per pak
                      </button>
                    ) : null}
                  </div>
                  {price ? (
                    <div className="text-sm tabular-nums shrink-0 opacity-80">
                      {price}
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  disabled={Boolean(mappingKey) || !hasPdp(opt)}
                  title={
                    hasPdp(opt)
                      ? undefined
                      : 'Geen productpagina — zoek opnieuw in Collect&Go'
                  }
                  className={
                    'w-full flex gap-2.5 items-start text-left rounded-md bg-white px-2 py-2 ring-1 ring-slate-200 hover:bg-slate-50 hover:ring-slate-300 disabled:opacity-50' +
                    (busyThis ? ' ring-violet-300' : '')
                  }
                  onClick={() => pickOrExplain(opt, null)}
                >
                  <CandidateThumb opt={opt} />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-sm font-medium leading-snug break-words">
                      {opt.display}
                    </div>
                    {meta ? (
                      <div className="text-xs opacity-60 mt-0.5">{meta}</div>
                    ) : null}
                    {!hasPdp(opt) ? (
                      <div className="text-xs text-amber-800 mt-0.5">
                        Geen productpagina — zoek opnieuw in Collect&Go
                      </div>
                    ) : null}
                  </div>
                  {price ? (
                    <div className="text-sm tabular-nums shrink-0 opacity-80">
                      {price}
                    </div>
                  ) : null}
                </button>
              )}
            </li>
          )
        })}
        <li>{searchControls}</li>
      </ul>
    )
  }

  if (status === 'pending' || (searching && !status)) {
    return <p className="text-sm opacity-50">Kandidaten zoeken…</p>
  }

  if (status === 'empty' || status === 'error') {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm opacity-50">
          {status === 'error' ? 'Zoeken mislukt' : 'Geen kandidaten gevonden'}
        </p>
        {searchControls}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm opacity-50">Nog geen kandidaten</p>
      {searchControls}
    </div>
  )
}

function CandidateThumb({ opt }: { opt: CogoBasketCandidate }) {
  return (
    <div className="h-11 w-11 rounded overflow-hidden shrink-0 flex items-center justify-center bg-white">
      {opt.hasImage !== false ? (
        <img
          src={articleImageSrc(opt.technicalProductId)}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
          onError={(e) => {
            ;(e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      ) : (
        <span className="text-[10px] opacity-40">geen foto</span>
      )}
    </div>
  )
}

function QtyEditForm({
  line,
  unit,
  draft,
  busy,
  inputRef,
  setDraft,
  onSubmit,
  onRecipe,
  onSkip,
  onCancel,
}: {
  line: CogoPlanLineView
  unit: string
  draft: string
  busy: boolean
  inputRef: RefObject<HTMLInputElement | null>
  setDraft: (v: string) => void
  onSubmit: () => void
  onRecipe: () => void
  onSkip: () => void
  onCancel: () => void
}) {
  return (
    <form
      className="flex flex-col gap-2 rounded-md bg-slate-50 ring-1 ring-slate-200 px-2.5 py-2"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <label className="text-xs opacity-60">
        Hoeveelheid ({unit || 'st'}) · recept {line.neededQuantity}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="number"
          min={0}
          inputMode="decimal"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
          className="w-28 rounded border border-slate-300 bg-white px-2 py-1.5 text-sm tabular-nums outline-hidden focus:ring-2 focus:ring-sky-300"
          aria-label={`Hoeveelheid ${line.product.display}`}
        />
        <span className="text-sm opacity-60">{unit}</span>
        <button
          type="submit"
          disabled={busy}
          className="text-xs rounded-md px-2.5 py-1.5 font-medium bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
        >
          Bewaar
        </button>
        <button
          type="button"
          disabled={busy}
          className={btnSecondary}
          onClick={onRecipe}
        >
          Recept
        </button>
        <button
          type="button"
          disabled={busy}
          className={btnSecondary}
          onClick={onSkip}
        >
          Overslaan
        </button>
        <button
          type="button"
          disabled={busy}
          className={btnSecondary}
          onClick={onCancel}
        >
          Annuleer
        </button>
      </div>
    </form>
  )
}

function PickRow({
  pick,
  busy,
  onNudge,
}: {
  pick: CogoPlanPick
  busy: boolean
  onNudge: (dir: 1 | -1) => void
}) {
  const unitPrice = formatCogoChoicePrice(pick)
  const total = formatMoney(pick.lineTotalCents)
  return (
    <LineGrid
      thumb={
        pick.hasImage ? (
          <img
            src={articleImageSrc(pick.technicalProductId)}
            alt=""
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="text-[10px] opacity-40">geen foto</span>
        )
      }
      main={
        <div>
          <div className="text-sm font-medium break-words">{pick.display}</div>
          <div className="text-xs opacity-60 flex flex-wrap gap-x-2 mt-0.5">
            <span>{pick.label}</span>
            {unitPrice ? <span>{unitPrice}</span> : null}
            {total ? <span className="tabular-nums">{total}</span> : null}
          </div>
          {pick.assortmentUrl ? (
            <a
              href={pick.assortmentUrl}
              target="_blank"
              rel="noreferrer"
              className={`${btnIcon} mt-1 inline-flex`}
              title="Open op Collect&Go"
              aria-label="Open op Collect&Go"
            >
              <CogoMark className="h-6 w-6" />
            </a>
          ) : null}
        </div>
      }
      qty={<ArticleQtyControls pick={pick} busy={busy} onNudge={onNudge} />}
    />
  )
}
